/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useEffectOnce } from '@utils/react/hooks';
import { getStreams, updateStream } from '@utils/graphql/creators/streams';
import { getZones } from '@utils/graphql/creators/zones';
import { getAppliances } from '@utils/graphql/creators/appliances';
import {
  Button,
  IconBadge,
  Modal,
  Table,
  ActionModal,
} from '@skyline-platform/alchemy';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { tabEventEmitter } from '../utils/TabEventEmitters';
import '../page.css';
import { StreamStepperFlow } from '@components/FormComponents/StreamStepperFlow/StreamStepperFlow';
import { StreamActions } from './StreamActions';
import { BulkImportForm } from '@components/FormComponents/BulkImportForm/BulkImportForm';
import { SearchBarComponent } from '@components/SearchBar/SearchBarComponent';
import {
  handleTableEvent,
} from '../utils/helpers';
import {
  buildSearchCriteria,
  buildCombinedCriteria,
  normalizeStringFields,
} from '../../utils/helpers';
import { getLogger } from '@utils/general';
import { getConfig } from '@utils/config';
import { StreamBulkActions } from '@components/BulkActions/StreamBulkActions';
import { StatusToggle } from '@components/StatusToggle/StatusToggle';
import PropTypes from 'prop-types';
import {
  dedupeRowsById,
  buildNestedFetchPatch,
} from '../utils/eventHelpers';
import { SkipToMainContent } from '@components/Misc';
import { useServerPagination } from '@utils/react/hooks/useServerPagination';
import { getMetadata } from '@utils/graphql/creators/metadata';
import { cache } from '../../../App';

const logger = getLogger('src.pages.streams.AllStreams');

const STREAM_FETCH_FIELDS = [
  'name',
  'description',
  'id',
  'appliance.name',
  'source.name',
  'zone.name',
  'online',
  'clspsAddress',
  'jurisdiction',
  // fields needed for stream edit
  'appliance.id',
  'appliance.management_address',
  'appliance.public_address',
  'source.id',
  'source.device.name',
  'source.appliance.name',
  'zone.id',
  'pull_on_request',
  'use_interleaved_rtsp_transport',
  'snapshot_interval',
];

// Maps frontend search field names to backend GraphQL criteria keys.
// Fields not in this map (source, appliance) are not searchable server-side.
const SEARCH_FIELD_MAP = {
  streamName: 'name',
  description: 'description',
  source: 'sourceName',
};

// Maps table column field names to backend sort field names.
const SORT_FIELD_MAP = {
  zone: 'zone_name',
  source: 'source_name',
  appliance: 'appliance_name',
  jurisdiction: 'jurisdiction',
};

// Maps filterable table column field names to backend criteria keys.
const FILTER_FIELD_MAP = {
  online: 'online',
  zone: 'zoneName',
  appliance: 'applianceName',
  jurisdiction: 'jurisdiction',
};

export const AllStreams = ({ userIsAdmin }) => {
  const operatorBulkActionsEnabled = getConfig().OPERATOR_STREAM_BULK_ACTIONS_ENABLED?.toLowerCase() === 'true';
  const showBulkActions = userIsAdmin || operatorBulkActionsEnabled;
  const streamStatusConfirmationEnabled = getConfig().STREAM_STATUS_CONFIRMATION_ENABLED?.toLowerCase() === 'true';

  const searchOptions = [
    { key: 'Stream Name', value: 'streamName' },
    { key: 'Description', value: 'description' },
    { key: 'Stream Profile', value: 'source' },
  ];
  const [selectedFields, setSelectedFields] = useState(searchOptions.map((option) => option.value));
  const [selectedStreamId, setSelectedStreamId] = useState();
  const [newStream, setNewStream] = useState(null);
  const [title, setTitle] = useState('');
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  // Stores full {id, name} objects fetched when the user clicks "select all" across all pages.
  // Cleared whenever individual row selection changes so bulk actions always use the right set.
  const [allSelectedRows, setAllSelectedRows] = useState([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  const jurisdictionFilterEnabled = getConfig().JURISDICTION_FILTER_ENABLED === 'true';

  const mapTableData = useCallback((streams) => {
    if (!Array.isArray(streams)) return [];
    return streams.map((stream) => {
      const streamNormalized = normalizeStringFields(stream, ['description']);

      return {
        ...streamNormalized,
        snapshot_interval: stream?.snapshot_interval,
        source: stream?.source?.name,
        zone: stream?.zone?.name,
        appliance: stream?.appliance?.name,
        jurisdiction: stream?.jurisdiction ?? '',
        action: { ...stream },
      };
    });
  }, []);

  const {
    rows,
    totalRows,
    loading,
    currentPage,
    rowsPerPage,
    handleSort,
    sortDirection,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    clearSearch,
    refetchCurrentPage,
    fetchAll,
    setRows,
    paginationResetKey,
  } = useServerPagination({
    fetchFn: getStreams,
    mapFn: mapTableData,
    fetchFields: STREAM_FETCH_FIELDS,
    defaultSort: { field: 'name', direction: 'asc' },
    sortFieldMap: SORT_FIELD_MAP,
    enableSort: true,
    onError: (error) => logger.error(error),
  });

  const nestedUpdateTimersRef = useRef(new Map());
  const rowsRef = useRef(rows);
  // Tracks full row objects for individually selected rows across all pages.
  // A new Map reference is created on each change so React detects the update.
  const [selectedRowObjects, setSelectedRowObjects] = useState(new Map());
  // Keeps a ref to allSelectedRows so handleSelectionChange can read the
  // previous select-all data when transitioning to individual mode.
  const allSelectedRowsRef = useRef([]);
  // Tracks the ID of a stream we just created via the form so that the concurrent
  // WebSocket create event can be suppressed (we handle the fetch ourselves).
  const pendingNewStreamIdRef = useRef(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    allSelectedRowsRef.current = allSelectedRows;
  }, [allSelectedRows]);

  const tableContext = {
    rowsRef,
    setRows,
    setSelectedRowIds,
    setSelectedId: setSelectedStreamId,
  };

  const applyStreamRows = useCallback((newRows) => {
    const normalizedRows = dedupeRowsById(newRows);
    setRows(normalizedRows);
  }, [setRows]);

  // Prefetch all IDs in the background whenever criteria/sort changes so that
  // clicking select-all is instant. The cache is keyed to fetchAll's identity
  // (which changes whenever criteria, sort, or fetchFn changes).

  // Store all zones and appliances for filter dropdowns (fetched directly)
  const [allZones, setAllZones] = useState([]);
  const [allAppliances, setAllAppliances] = useState([]);
  // Cache for select-all bulk actions (not used for filter dropdowns)
  const selectAllCacheRef = useRef(null);
  // Resolvers for pending stream status updates — keyed by SFS tracking_id.
  // Resolved by the sub_channel message intercepted in handleEvent.
  const pendingStatusUpdatesRef = useRef(new Map());
  // Fetch all zones for filter dropdown
  useEffect(() => {
    let cancelled = false;
    async function fetchZones() {
      const { query, execute } = getZones(null, {}, { pagination: { count_per_page: 10000 } });
      query.addReturnProperties('id', 'name');
      const result = await execute();
      if (!cancelled && result?.data) {
        setAllZones(
          (Array.isArray(result.data) ? result.data : []).map((z) => ({ label: z.name, value: z.name }))
        );
      }
    }
    fetchZones();
    return () => { cancelled = true; };
  }, []);

  // Fetch all appliances for filter dropdown
  useEffect(() => {
    let cancelled = false;
    async function fetchAppliances() {
      const { query, execute } = getAppliances(null, {}, { pagination: { count_per_page: 10000 } });
      query.addReturnProperties('id', 'name');
      const result = await execute();
      if (!cancelled && result?.data) {
        setAllAppliances(
          (Array.isArray(result.data) ? result.data : []).map((a) => ({ label: a.name, value: a.name }))
        );
      }
    }
    fetchAppliances();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!jurisdictionFilterEnabled) return;
    let cancelled = false;
    async function fetchJurisdictions() {
      try {
        const { execute } = await getMetadata({ key: 'regex:jurisdiction' });
        await execute().then(({ error, data }) => {
          if (error) logger.error(error);
          if (!cancelled && data && data.length > 0) {
            const uniqueOptions = Array.from(
              new Set(data.map((j) => j.value).filter(Boolean))
            )
              .sort()
              .map((value) => ({ label: value, value }));
            setJurisdictionOptions(uniqueOptions);
          }
        });
      } catch (err) {
        logger.error('Failed to fetch jurisdictions', err);
      }
    }
    fetchJurisdictions();
    return () => { cancelled = true; };
  }, []);

  const handleSelectAll = useCallback(async () => {
    if (!selectAllCacheRef.current) {
      // Only fetch minimal fields needed for bulk actions
      selectAllCacheRef.current = await fetchAll(['id', 'name']);
    }
    const all = selectAllCacheRef.current;
    setSelectedRowObjects(new Map());
    setAllSelectedRows(all);
    setSelectedRowIds(all.map((r) => r.id));
  }, [fetchAll]);

  const handleSelectionChange = useCallback((ids) => {
    const idSet = new Set(ids.map(String));
    setSelectedRowObjects((prev) => {
      // When transitioning from select-all to individual mode the Map is empty
      // but allSelectedRowsRef still holds {id, name} for every record.
      // Seed the Map so cross-page selections aren't lost.
      const next = prev.size === 0 && allSelectedRowsRef.current.length > 0
        ? new Map(allSelectedRowsRef.current.map((r) => [String(r.id), r]))
        : new Map(prev);
      for (const id of next.keys()) {
        if (!idSet.has(id)) next.delete(id);
      }
      for (const row of rowsRef.current ?? []) {
        if (idSet.has(String(row.id))) next.set(String(row.id), row);
      }
      return next;
    });
    setSelectedRowIds(ids);
    setAllSelectedRows([]); // individual change — clear the bulk-fetched set
  }, []);

  const hasActiveCriteriaRef = useRef(false);
  const activeFiltersRef = useRef({});
  const [activeFiltersState, setActiveFiltersState] = useState({});
  const lastSearchCriteriaRef = useRef({});

  const applyCombinedCriteria = useCallback((searchCriteria, columnFilters) => {
    const combined = buildCombinedCriteria(searchCriteria, columnFilters, FILTER_FIELD_MAP);
    const hasCriteria = Object.keys(combined).length > 0;
    if (hasCriteria) {
      hasActiveCriteriaRef.current = true;
      handleSearch(combined);
    } else if (hasActiveCriteriaRef.current) {
      hasActiveCriteriaRef.current = false;
      clearSearch();
    }
  }, [handleSearch, clearSearch]);

  const handleServerSearch = useCallback((searchTerm, fields) => {
    if (!searchTerm) {
      lastSearchCriteriaRef.current = {};
      applyCombinedCriteria({}, activeFiltersRef.current);
      return;
    }
    const criteria = buildSearchCriteria(searchTerm, fields, SEARCH_FIELD_MAP);
    if (Object.keys(criteria).length === 0) return;
    lastSearchCriteriaRef.current = criteria;
    applyCombinedCriteria(criteria, activeFiltersRef.current);
  }, [applyCombinedCriteria]);

  const handleFilterChangeFromTable = useCallback((columnFilters) => {
    activeFiltersRef.current = columnFilters;
    setActiveFiltersState(columnFilters);
    applyCombinedCriteria(lastSearchCriteriaRef.current, columnFilters);
  }, [applyCombinedCriteria]);

  // Locates stream row by id and gives it select state
  const handleSelectedTab = (eventObject) => {
    const streamId = eventObject.detail;
    setSelectedStreamId(streamId);
  };

  const handleEvent = async (eventMessage) => {
    // Intercept SFS sub_channel responses for pending individual status updates.
    // These messages have message_id directly on detail (unlike mailbox messages).
    if (eventMessage?.detail?.message_id) {
      const channel = eventMessage.detail.channel;
      const resolve = pendingStatusUpdatesRef.current.get(channel);
      if (resolve) {
        const success = eventMessage.detail.data?.response_code < 1100;
        resolve(success ? {} : { error: 'Failed to update stream status.' });
        pendingStatusUpdatesRef.current.delete(channel);
        cache.delete(channel);
      }
      return;
    }
    // AllStreams uses handleTableEvent because its rows are affected by multiple model types:
    // a zone rename, appliance name change, or source rename all need to ripple into stream
    // rows that reference those entities. handleTableEvent handles that fan-out — debouncing
    // nested patches, resolving linked-id changes, and dispatching create/remove/update actions.
    // Pages whose events only ever affect their own rows (AllZones, AllAppliances, AllApiKeys)
    // use inline patchRow/removeRow/addRow instead, which is simpler for that case.
    //
    // Guard: only process models this page displays or nests. Appliance
    // connection_count storms (hundreds of events/min) must not trigger table work.
    const model = eventMessage?.detail?.payload?.model?.toLowerCase();
    if (model && ![
      'stream',
      'appliance',
      'zone',
      'source',
    ].includes(model)) return;
    // Suppress the WebSocket create event for a stream we just created via the
    // form — the newStream effect handles fetching the correct page itself.
    const parsed = eventMessage?.detail?.payload ? JSON.parse(eventMessage.detail.payload.data ?? '{}') : null;
    if (
      pendingNewStreamIdRef.current &&
      eventMessage?.detail?.payload?.action?.toLowerCase() === 'create' &&
      eventMessage?.detail?.payload?.model?.toLowerCase() === 'stream' &&
      String(parsed?.current?.id) === String(pendingNewStreamIdRef.current)
    ) {
      return;
    }
    await handleTableEvent(eventMessage, {
      sourceModel: 'stream',
      fetchFields: STREAM_FETCH_FIELDS,
      mapTableData,
      tableContext,
      applyRows: applyStreamRows,
      nestedUpdateTimersRef,
      buildFetchPatch: buildNestedFetchPatch([
        'zone',
        'source',
        'appliance',
      ]),
    });
  };

  /***** SUBSCRIPTIONS *****/
  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    messageHandler.subscribe(handleEvent);

    return () => {
      messageHandler.unsubscribe(handleEvent);
    };
  });

  useEffectOnce(() => {
    tabEventEmitter.on('selectTab', handleSelectedTab);

    return () => {
      tabEventEmitter.off('selectTab', handleSelectedTab);
    };
  });
  /***** END SUBSCRIPTIONS *****/

  useEffect(() => {
    const run = async () => {
      if (newStream) {
        tabEventEmitter.emit('updateTabs', newStream.id);
        setSelectedStreamId(newStream.id);
        pendingNewStreamIdRef.current = newStream.id;
        // Retry with increasing delays — the backend may not have indexed the new
        // record immediately. Starting at 300ms avoids a wasted instant call.
        let all = [];
        let index = -1;
        const delays = [
          300,
          600,
          1200,
        ];
        for (let attempt = 0; attempt < delays.length && index === -1; attempt++) {
          await new Promise((r) => setTimeout(r, delays[attempt]));
          all = await fetchAll(['id']);
          index = all.findIndex((r) => String(r.id) === String(newStream.id));
        }
        if (index >= 0) {
          const targetPage = Math.ceil((index + 1) / rowsPerPage);
          if (targetPage !== currentPage) {
            handlePageChange(targetPage);
          } else {
            refetchCurrentPage();
          }
        } else {
          refetchCurrentPage();
        }
        pendingNewStreamIdRef.current = null;
      }
    };
    run();
  }, [newStream]);

  const tableColumns = useMemo(() => [
    {
      field: 'online',
      headerName: 'Status',
      cell: (row) => {
        const isOnline = row?.online ?? false;
        return (
          <StatusToggle
            online={isOnline}
            entityId={row?.id}
            anchorPrefix='--stream-toggle-'
            showConfirmation={streamStatusConfirmationEnabled}
            onConfirm={streamStatusConfirmationEnabled
              ? async (currentOnline) => {
                const result = await updateStream('update_stream', row?.id, { online: !currentOnline }).execute();
                // In dev mode execute() returns mock data with no tracking_id — resolve immediately.
                if (!result?.tracking_id) return result;
                // Mark this entry so handleSingleStreamUpdate suppresses the toaster —
                // the popover provides feedback instead. Runs synchronously before any
                // SFS WebSocket response can arrive, so there is no race condition.
                const existing = cache.get(result.tracking_id);
                if (existing) cache.set(result.tracking_id, { ...existing, suppressNotification: true });
                return new Promise((resolve) => {
                  pendingStatusUpdatesRef.current.set(result.tracking_id, resolve);
                });
              }
              : (currentOnline) => updateStream('update_stream', row?.id, { online: !currentOnline }).execute()}
            triggerIcon={(
              <IconBadge
                label={`Stream ${isOnline ? 'Online' : 'Offline'}`}
                iconClassName={isOnline ? 'fa-solid fa-video' : 'fa-light fa-video-slash'}
                srText={isOnline ? 'Stream Online' : 'Stream Offline'}
                className='stream-status-badge'
              />
            )}
            triggerAriaLabel={isOnline
              ? 'Stream online — click to change status'
              : 'Stream offline — click to change status'}
          />
        );
      },
      width: '130px',
      sortable: true,
      filterable: true,
      filterOptions: [{ label: 'Online', value: true }, { label: 'Offline', value: false }],
    },
    {
      field: 'name',
      headerName: 'Stream Name',
      cell: (row) => (
        <a
          tabIndex={0}
          className={`stream-name ${row.__selected ? 'selected-tab' : ''}`}
          id={`datacell-${row?.id || Date.now()}`}
          aria-label={`View details for stream ${row?.name}`}
          title={row?.name}
          onClick={() => {
            tabEventEmitter.emit('updateTabs', row?.id);
            setSelectedStreamId(row?.id);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              tabEventEmitter.emit('updateTabs', row?.id);
              setSelectedStreamId(row?.id);
              event.preventDefault();
            }
          }}
        >
          {row?.name}
        </a>
      ),
      grow: 1,
      sortable: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      type: 'string',
      grow: 1,
      sortable: true,
    },
    {
      field: 'snapshot_interval',
      headerName: 'Snapshot Interval',
      grow: 1,
      sortable: true,
    },
    {
      field: 'source',
      headerName: 'Stream Profile',
      type: 'string',
      grow: 1,
      sortable: true,
    },
    {
      field: 'zone',
      headerName: 'Zone',
      type: 'string',
      grow: 0.8,
      sortable: true,
      filterable: true,
      filterOptions: allZones,
    },
    {
      field: 'appliance',
      headerName: 'Appliance',
      type: 'string',
      grow: 0.8,
      sortable: true,
      filterable: true,
      filterOptions: allAppliances,
    },
    {
      field: 'jurisdiction',
      headerName: 'Jurisdiction',
      type: 'string',
      grow: 1,
      sortable: jurisdictionFilterEnabled,
      filterable: jurisdictionFilterEnabled,
      filterOptions: jurisdictionFilterEnabled ? jurisdictionOptions : [],
    },
    {
      field: 'action',
      headerName: '',
      cell: (row) => (
        <ActionModal
          className='stream-row-actions'
          id={row.name}
          title='Toggle Options'
          content={StreamActions({
            rowInfo: row.action, id: row.id, userIsAdmin,
          })}
        />
      ),
      width: '50px',
      sortable: false,
    },
  ], [
    selectedStreamId,
    allZones,
    allAppliances,
    jurisdictionOptions,
    jurisdictionFilterEnabled,
    streamStatusConfirmationEnabled,
  ]);

  // When select-all was used, allSelectedRows has the full cross-page set from fetchAll.
  // For individual checkbox selection, selectedRowObjects accumulates row objects as the
  // user selects across pages — rows alone can't be used because it only contains the current page.
  const selectedStreams = useMemo(() => {
    if (allSelectedRows.length > 0) return allSelectedRows;
    return [...selectedRowObjects.values()];
  }, [allSelectedRows, selectedRowObjects]);

  // Create a shallow clone of data to trigger row re-render without pagination reset (key)
  const displayRows = useMemo(() => {
    return Array.isArray(rows)
      ? rows.map((item) => ({
        ...item,
        __selected: item.id === selectedStreamId,
      }))
      : [];
  }, [rows, selectedStreamId]);

  return (
    <gar-all-streams>
      <div className='actions-bar'>
        {showBulkActions && (
          <ActionModal
            id='streamsBulkAction'
            triggerElement={
              <div id='bulk-action-btn' className='bulk-action-btn'>
                Bulk Actions
                <i aria-hidden={true} className='fa fa-angle-down' />
              </div>
            }
            content={<StreamBulkActions streams={selectedStreams} userIsAdmin={userIsAdmin} />}
            closeOntab={false}
          />
        )}
        <SearchBarComponent
          searchOptions={searchOptions}
          userIsAdmin={userIsAdmin}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          fieldName='Streams'
          onSearch={handleServerSearch}
        />
        {userIsAdmin && (
          <>
            <Modal
              className='bulk-import-modal'
              closeIcon
              headerPlacement='headerLeft'
              header={
                <div className='bulk-import-title'>
                  <i className='fa-solid fa-upload'></i>Bulk Import
                </div>
              }
              trigger={({ handleOpen }) => (
                <Button
                  label='Bulk Import'
                  iconLeft='fa-solid fa-upload'
                  state='default'
                  type='text'
                  onClick={handleOpen}
                />
              )}
              slots={{
                content: (handleClose) => (
                  <BulkImportForm handleClose={handleClose} />
                ),
              }}
            />
            <Modal
              className='new-stream-modal'
              closeIcon
              header={title}
              trigger={({ handleOpen }) => (
                <Button
                  label='New Stream'
                  iconLeft='fa-solid fa-circle-plus'
                  state='default'
                  type='filled'
                  onClick={handleOpen}
                />
              )}
              slots={{
                content: (handleClose) => (
                  <StreamStepperFlow
                    handleClose={handleClose}
                    setNewStream={setNewStream}
                    setTitle={setTitle}
                  />
                ),
              }}
            />
          </>
        )}
      </div>
      <SkipToMainContent
        text="Skip to pagination controls"
        targetSelector="alc-pagination-bar"
      />
      <div>
        <Table
          key={paginationResetKey}
          tableName='Streams'
          // prevents additional focus on the table itself in firefox
          tabIndex={-1}
          columns={tableColumns}
          getRowId={(row) => row.id}
          hideInColumnMenu={['action']}
          rows={displayRows}
          style={{ height: displayRows.length === 0 && !loading ? '400px' : undefined }}
          initialSortField='name'
          defaultSortAsc={true}
          sortServer
          onSort={handleSort}
          onFilterChange={handleFilterChangeFromTable}
          defaultColumnFilters={activeFiltersState}
          paginationServer
          paginationTotalRows={totalRows}
          currentPage={currentPage}
          paginationPerPage={rowsPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          progressPending={loading}
          sortDirection={sortDirection}
          selectedId={selectedStreamId}
          checkboxSelection={showBulkActions}
          selectedRowIds={selectedRowIds}
          onSelectionChange={showBulkActions ? handleSelectionChange : undefined}
          onSelectAll={showBulkActions ? handleSelectAll : undefined}
        />
      </div>
    </gar-all-streams>
  );
};

AllStreams.propTypes = {
  userIsAdmin: PropTypes.bool.isRequired,
};
